// TYPE-ONLY on purpose: the provenance DTO is declared beside the badge union it
// belongs to, and a type import is erased, so re-exporting this module from that
// same barrel creates no runtime cycle.
import type { AskHelixFigureProvenanceDto } from '../../ask-helix';

// ─────────────────────────────────────────────────────────────────────────────
// METRIC SERIES — THE TREND BLOCK, ON THE SAME THREE RULES.
//
// A read that already publishes a figure PER PERIOD owes the reader the shape
// of the move, not just the latest level. Until now the only way a trajectory
// reached glass was as prose, so the surface either re-derived the points from
// the sentence or showed nothing.
//
// This is that block, and it obeys rule 1 above without exception: a producer
// names the series, its labels and its ordering, and every FIGURE it carries is
// a `placeholder` into an engine-bound `numericBindings[]` entry on the same
// envelope. The producer states no number of its own, so the anti-fabrication
// fence sees exactly the numbers it already sees — a series point that is not a
// published governed binding cannot exist.
//
// The public block is minted from that spec by the manager's ONE display
// authority (`managerDisplayValue`), so a point's `displayValue` is the same
// string the bindings table and the prose print for the identical fact. The
// client never formats, never re-derives and never re-rounds.
// ─────────────────────────────────────────────────────────────────────────────

/**
 * What a series item IS relative to its group: the actual measured line, the
 * plan/projection line, or the target line. Closed, because a renderer styles
 * on it (solid vs dashed vs marker) and an open string would silently fall
 * through to a default.
 */
export const ASK_HELIX_METRIC_SERIES_ROLES = Object.freeze([
  'plan',
  'actual',
  'target',
] as const);

export type AskHelixMetricSeriesRoleDto =
  (typeof ASK_HELIX_METRIC_SERIES_ROLES)[number];

/** One point of the PRODUCER'S spec: a period label and the figure that fills it. */
export type GovernedMetricSeriesPointSpec = {
  /** Reader-facing period label, producer-authored ("FY2023"). */
  label: string;
  /**
   * The engine-bound `numericBindings[]` placeholder carrying this point's
   * figure. The producer publishes the binding; this only names it.
   */
  placeholder: string;
  /**
   * ORDERING AUTHORITY, and never a figure (#5446 P1-3). The calendar day this
   * point is stamped with, as `YYYY-MM-DD`.
   *
   * A group's shared axis has to be chronological across every line in it, and a
   * formatted reader label cannot decide that: taking the plan labels in order
   * and appending unseen actual labels puts a mid-period reading ("15 Jul 2026")
   * after the last plan point ("30 Sep 2026") and lets a multi-reading line run
   * backward through time. Sorting the LABEL is no better — it is prose, in the
   * producer's chosen wording.
   *
   * Optional because not every series is dated: a fiscal-year momentum line
   * orders by its own producer sequence and omits this. A renderer that has it
   * on every point of a group builds the axis from it; otherwise it keeps
   * producer order.
   */
  pointAsOf?: string;
};

/**
 * The producer's series spec, carried on the governed tool output envelope.
 * Reader-safe strings and placeholders only — it holds no figure at all.
 */
export type GovernedMetricSeriesSpec = {
  /** Producer-stable identity for this line ("operating_income_momentum_fy"). */
  seriesId: string;
  /** What is plotted, in the reader's words. */
  metricLabel: string;
  unit: string | null;
  /** The span the whole series covers, and the governed cut it was read at. */
  timeframe?: { label: string; asOf: string | null } | null;
  /** The governed scope the series is read for ("Enterprise"). */
  scope?: { label: string } | null;
  /** Chronological, producer order; the renderer never re-sorts. */
  points: readonly GovernedMetricSeriesPointSpec[];
  /** The governed source class, copied from the producer's own declaration. */
  sourceClassLabel: string | null;
  /** Series sharing a group render in ONE card, in server order. */
  groupId?: string;
  role?: AskHelixMetricSeriesRoleDto;
  // NO PROSE CONCLUSION (#5446 P1-4). A producer-authored sentence here would be a
  // SECOND public narration authority beside `answerMarkdown` — free to duplicate it,
  // to contradict it, and to speak on a path where narration itself failed. A producer
  // that has something to say about the shape of its line says it as narration input to
  // the one narrator, which is where every other reader sentence comes from.
};

/**
 * A published point. `displayValue` is the ONLY string a surface prints for
 * this figure, and `value` is GEOMETRY — it exists so a chart can place the
 * point, never so a client can format it.
 *
 * A point whose placeholder resolved to no single governed fact publishes
 * `displayValue: null` with no `value`, exactly as an ambiguous narration token
 * publishes `— (not computed)`. That is a disclosed absence, never a zero.
 */
export type AskHelixMetricSeriesPointDto = {
  label: string;
  displayValue: string | null;
  /** Omitted, never null and never non-finite, when there is nothing to plot. */
  value?: number;
  /** The badge of the binding this point was minted from. */
  provenance?: AskHelixFigureProvenanceDto;
  /**
   * The producer's ordering key for this point (`YYYY-MM-DD`), passed through
   * unchanged. A surface drawing several lines in one group orders the shared
   * axis by THIS, never by the reader label. Omitted when the producer states
   * none, exactly as it is on the spec.
   */
  pointAsOf?: string;
};

/**
 * The public trend block. Present only when a producer emitted a spec (Law 3);
 * OMITTED, never null.
 */
export type AskHelixMetricSeriesDto = {
  seriesId?: string;
  metricLabel: string;
  unit: string | null;
  timeframe?: { label: string; asOf: string | null } | null;
  scope?: { label: string } | null;
  points: AskHelixMetricSeriesPointDto[];
  /**
   * FALLBACK ONLY — the per-point badge is the truth. A history point read from
   * an approved filing and a projection point from an approved formula are not
   * equally sourced, and one series-level badge would have to lie about one of
   * them. This carries the WEAKEST class present so a renderer that has room
   * for a single chip understates rather than overstates.
   */
  provenance: AskHelixFigureProvenanceDto | null;
  sourceClassLabel: string | null;
  /**
   * Producer-authored reader prose about the shape of the line. No producer
   * emits one today — the finance momentum series leaves it absent — and the
   * slot exists so a producer that has a governed close can deliver it without
   * reopening the egress allowlist.
   */
  insight?: { text: string; provenance: AskHelixFigureProvenanceDto } | null;
  groupId?: string;
  role?: AskHelixMetricSeriesRoleDto;
};
